import compact from 'lodash/fp/compact';

import React from 'react';
import { FormattedMessage, FormattedNumber, FormattedTime } from 'react-intl';
import DeviceRolloutState from '~/features/devices/components/DeviceRolloutState';
import DeviceRegistrationMethod from '~/features/devices/components/DeviceRegistrationMethod';
import DeviceOffline from '~/features/devices/components/DeviceOffline';

import GroupChipList from '~/features/groups/components/GroupChipList';
import FieldOrNoData from '~/features/base/components/FieldOrNoData';
import MmtProperties from '~/features/devices/components/MmtProperties';
import LastRolloutStateItem from '~/features/devices/components/LastRolloutStateItem';
import EulaStatus from '~/features/devices/components/EulaStatus';

import { env } from '~/env';
import classNames from 'classnames';
import { updateOutcome } from '~/features/devices/constants/deviceRolloutStates';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import some from 'lodash/fp/some';
import LastReportedBundle from '~/features/devices/components/LastReportedBundle';
import { copyToClipboard } from '~/features/apps/utils/appsUtils';
import IvdField from '~/features/devices/components/IvdField';

const { networkCodes } = env.runtimeConfig;
const { showRolloutState } = env.featureToggles;

/**
 * TODO Move somewhere else, be it data, together with tiles.
 */
export const cm4DeviceRowDescriptors = compact([
{
    field: 'hwVariant',
    label: 'intl-msg:hwVariant',
    format: field => <FieldOrNoData field={field}/>,
}, {
    field: 'vehicleVariant',
    label: 'intl-msg:vehicleVariant',
    format: field => <FieldOrNoData field={field}/>,
}, {
    field: 'vehicleGroupNames',
    label: 'intl-msg:groups',
    format: field => <GroupChipList groups={field}/>,
}, {
    field: 'lastCheckForUpdateTimestamp',
    label: 'intl-msg:lastCheckForUpdateTimestamp',
    format: field => {
        return (
            <span className='white-space-nowrap'>
                <FieldOrNoData field={field ? <FormattedTime value={field} year='numeric'
                                                             month='2-digit' day='2-digit'/> : null}/>
                <DeviceOffline timestamp={field}/>
            </span>
        );
    },
}, {
    field: 'lastNetworkCodeReported',
    label: 'intl-msg:deviceNetworkLocation',
    format: (field, data) => {
        let lastNetworkReportedCountry;
        let countryCode;
        if (field) {
            countryCode = parseInt(field.split(' ')[0], 10);
            lastNetworkReportedCountry = networkCodes[countryCode]?.name || '';
        }
        const validNetworkCode = some((networkCode) => parseInt(networkCode.replace(/\D/g, ''), 10) === countryCode, data.networkCodeList);
        return (
            <span>
                {
                    lastNetworkReportedCountry ? lastNetworkReportedCountry
                        : <FormattedMessage id='intl-msg:networkCountryCode' values={{ countryCode: countryCode }}/>
                }
                <OverlayTrigger placement='top' overlay={

                    <Tooltip id='tooltip' className='top-right'>
                        {
                            countryCode && validNetworkCode ?
                                <FormattedMessage id='intl-msg:deviceNetworkCanUseServices'/>
                                :
                                <FormattedMessage id='intl-msg:report.reason.disallowedNetwork'/>
                        }
                    </Tooltip>}>
                            <span className={classNames('label-condensed margin-right-5')}>
                            {
                                countryCode && validNetworkCode ?
                                    <span
                                        className='text-color-success uikit-icon icon-ok-sign margin-right-10 text-size-16'/>
                                    :
                                    <span
                                        className='text-color-danger uikit-icon icon-error-sign margin-right-10 text-size-16'/>
                            }
                            </span>
                        </OverlayTrigger>
            </span>
        );
    },
}, {
    field: 'lastMileageReported',
    label: 'intl-msg:mileageWithUnit',
    format: field => {
        return field ? <FieldOrNoData field={<FormattedNumber value={field}/>}/> : <FieldOrNoData field={field}/>;
    },
}, {
    field: 'description',
    label: 'intl-msg:description',
    format: field => <FieldOrNoData field={field}/>,
}, {
    field: 'lastRolloutState',
    label: 'intl-msg:lastRolloutState',
    format: field => <LastRolloutStateItem field={field}/>,
}, {
        field: 'ivd',
        label: 'intl-msg:ivd',
        labelTooltip: (
            <OverlayTrigger
                key={1}
                placement={'right-start'}
                overlay={
                    <Tooltip id='tooltip' allowOnTouch>
                        <FormattedMessage id='intl-msg:ivdTooltip'/>
                    </Tooltip>
                }
            >
                <span className="uikit-icon icon-info-sign margin-left-10 label-info" style={{fontSize: '18px'}} aria-hidden="true"></span>
            </OverlayTrigger>
        ),
        format: (field, item) => <IvdField field={field} item={item}/>

    }, {
        field: 'lastEssentialImageReported',
        label: 'intl-msg:lastReportedEssentialImage',
        format: (field, data) => {
            return <LastReportedBundle serialNumber={data.serialNumber} deliverableId={field?.id}
                                       currentVersion={field?.version}/>;
        },
    },{
    field: 'vehicleManufacturerSparePartNumber',
    label: 'intl-msg:manufacturerSparePartNumber',
    format: field => <FieldOrNoData field={field}/>,
}, {
    field: 'vin',
    label: 'intl-msg:VIN',
    format: field => <FieldOrNoData field={field}/>,
},
    {
    field: 'shortVin',
    label: 'intl-msg:shortVin',
    format: field => <FieldOrNoData field={field}/>,
}, {
    field: 'assetId',
    label: 'intl-msg:assetId',
    format: field => <FieldOrNoData field={field}/>,
}, {
    field: 'lastFleetEvent',
    label: 'intl-msg:lastFleetEvent',
    format: field => <FieldOrNoData field={field ? <FormattedTime value={field} year='numeric'
                                                                  month='2-digit' day='2-digit'/> : null}/>,
}, {
    field: 'registrationMethod',
    label: 'intl-msg:registrationMethod',
    format: field => <DeviceRegistrationMethod registrationMethod={field}/>,
}, {
    field: 'registrationTimestamp',
    label: 'intl-msg:registrationTimestamp',
    format: field => <FieldOrNoData field={field ? <FormattedTime value={field} year='numeric'
                                                                  month='2-digit' day='2-digit'/> : null}/>,
}, showRolloutState && {
    field: 'rolloutState',
    label: 'intl-msg:rolloutState',
    format: field => <DeviceRolloutState rolloutState={field}/>,
}, {
        field: 'mmtProperties',
        label: 'intl-msg:mmtProperties',
        format: field => <MmtProperties mmtProperties={field} />,
    }, {
        field: 'eulaStatus',
        label: 'intl-msg:eulaStatus',
        format: field => <EulaStatus eulaStatus={field}/>,
    }]);
